import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import gregorian from 'react-date-object/calendars/gregorian';
import JobTitlesAdmin from './JobTitlesAdmin';

const EMPTY_FILTERS = {
  search: '',
  job_id: '',
  rating: '',
  education: '',
  address: '',
  employment_type: '',
  has_resume: '',
  date_from: '',
  date_to: ''
};

const toApiDate = (date) => {
  if (!date) return '';
  return new DateObject(date).convert(gregorian).format('YYYY-MM-DD');
};

const toJalaliPickerValue = (apiDate) => {
  if (!apiDate) return null;
  return new DateObject({
    date: apiDate,
    format: 'YYYY-MM-DD',
    calendar: gregorian
  }).convert(persian);
};

const parseSkills = (skills) => {
  if (!skills) return [];
  try {
    const parsed = JSON.parse(skills);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatSalary = (value) => {
  const num = parseInt(value, 10);
  if (Number.isNaN(num)) return '—';
  return `${num.toLocaleString('fa-IR')} تومان`;
};

const formatDate = (value) => {
  if (!value) return 'نامشخص';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'نامشخص';
  return date.toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const ApplicationCard = ({
  app,
  expanded,
  onToggle,
  onUpdateRating,
  onUpdateNotes,
  getRatingColor,
  getTextColor
}) => {
  const skills = parseSkills(app.skills);

  return (
    <article
      className={`app-card ${expanded ? 'is-expanded' : ''}`}
      style={{ borderRightColor: getRatingColor(app.rating) }}
    >
      <div className="app-card-summary" onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        {app.photo_path ? (
          <img src={app.photo_path} alt="" className="app-photo" />
        ) : (
          <div className="app-photo app-photo-placeholder">{(app.full_name || '?').charAt(0)}</div>
        )}

        <div className="app-card-main">
          <div className="app-card-title-row">
            <h3>{app.full_name}</h3>
            <span
              className="rating-badge"
              style={{
                backgroundColor: getRatingColor(app.rating),
                color: getTextColor(app.rating)
              }}
            >
              {app.rating || 'بدون ارزیابی'}
            </span>
          </div>

          <div className="app-card-meta">
            <span>{app.desired_job || 'بدون عنوان شغلی'}</span>
            <span>{app.mobile}</span>
            <span>{app.employment_type}</span>
            <span>{formatDate(app.created_at)}</span>
          </div>

          <div className="app-card-quick">
            {app.resume_path && (
              <a
                href={app.resume_path}
                target="_blank"
                rel="noopener noreferrer"
                className="resume-link"
                onClick={(e) => e.stopPropagation()}
              >
                مشاهده رزومه
              </a>
            )}
            <button
              type="button"
              className="toggle-details-btn"
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
            >
              {expanded ? 'بستن جزئیات' : 'مشاهده جزئیات'}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="app-card-body">
          <div className="app-detail-grid">
            <div className="detail-block">
              <h4>اطلاعات شخصی</h4>
              <dl>
                <div><dt>تاریخ تولد</dt><dd>{formatDate(app.dob)}</dd></div>
                <div><dt>جنسیت</dt><dd>{app.gender || '—'}</dd></div>
                <div><dt>موبایل</dt><dd>{app.mobile || '—'}</dd></div>
                <div><dt>ایمیل</dt><dd>{app.email || '—'}</dd></div>
                <div className="full"><dt>آدرس</dt><dd>{app.address || '—'}</dd></div>
              </dl>
            </div>

            <div className="detail-block">
              <h4>تحصیلات و شغل</h4>
              <dl>
                <div><dt>شغل درخواستی</dt><dd>{app.desired_job || '—'}</dd></div>
                <div><dt>تحصیلات</dt><dd>{app.education || '—'}</dd></div>
                <div><dt>رشته</dt><dd>{app.field || '—'}</dd></div>
                <div><dt>دانشگاه</dt><dd>{app.university || '—'}</dd></div>
                <div><dt>آخرین شغل</dt><dd>{app.last_job_title || '—'}</dd></div>
                <div><dt>محل کار</dt><dd>{app.last_workplace || '—'}</dd></div>
                <div><dt>مدت اشتغال</dt><dd>{app.last_job_duration || '—'}</dd></div>
                <div><dt>نوع استخدام</dt><dd>{app.employment_type || '—'}</dd></div>
                <div><dt>شیفت کاری</dt><dd>{app.preferred_shift || '—'}</dd></div>
                <div><dt>انتظار حقوقی</dt><dd>{formatSalary(app.salary_expectation)}</dd></div>
              </dl>
            </div>
          </div>

          {skills.length > 0 && (
            <div className="detail-block">
              <h4>مهارت‌ها</h4>
              <div className="skill-chips">
                {skills.map((skill, index) => (
                  <span key={`${skill}-${index}`} className="skill-chip">{skill}</span>
                ))}
              </div>
            </div>
          )}

          <div className="detail-block">
            <h4>زبان‌ها</h4>
            <div className="lang-summary">
              <p>
                <strong>انگلیسی:</strong> خواندن {app.english_reading || '—'}،
                نوشتن {app.english_writing || '—'}،
                مکالمه {app.english_speaking || '—'}
              </p>
              <p>
                <strong>عربی:</strong> خواندن {app.arabic_reading || '—'}،
                نوشتن {app.arabic_writing || '—'}،
                مکالمه {app.arabic_speaking || '—'}
              </p>
            </div>
          </div>

          <div className="app-actions">
            <div className="rating-section">
              <label htmlFor={`rating-${app.id}`}>سطح ارزیابی</label>
              <select
                id={`rating-${app.id}`}
                className="rating-select"
                value={app.rating || ''}
                onChange={(e) => onUpdateRating(app.id, e.target.value)}
                style={{
                  backgroundColor: getRatingColor(app.rating),
                  color: getTextColor(app.rating)
                }}
              >
                <option value="">انتخاب کنید</option>
                <option value="ضعیف">ضعیف</option>
                <option value="متوسط">متوسط</option>
                <option value="خوب">خوب</option>
                <option value="عالی">عالی</option>
              </select>
            </div>

            <div>
              <label htmlFor={`notes-${app.id}`}>یادداشت ادمین</label>
              <textarea
                id={`notes-${app.id}`}
                className="description-textarea"
                placeholder="یادداشت خود را بنویسید..."
                defaultValue={app.admin_notes || ''}
                onBlur={(e) => onUpdateNotes(app.id, e.target.value)}
              />
              <p className="field-hint">با خروج از فیلد ذخیره می‌شود</p>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

const AdminPanel = () => {
  const [applications, setApplications] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalApplications, setTotalApplications] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('applications');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    if (activeTab === 'applications') {
      loadJobTitles();
      loadApplications();
    }
  }, [currentPage, perPage, navigate, activeTab, appliedFilters]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadJobTitles = async () => {
    try {
      const response = await axios.get('/api/admin/job-titles', {
        headers: getAuthHeaders()
      });
      setJobTitles(response.data || []);
    } catch (error) {
      console.error('Error loading job titles:', error);
    }
  };

  const buildFilterParams = (filterValues) => {
    const params = new URLSearchParams({
      page: String(currentPage),
      per_page: String(perPage)
    });

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value !== '' && value != null) {
        params.set(key, value);
      }
    });

    return params.toString();
  };

  const loadApplications = async () => {
    setIsLoading(true);
    try {
      const query = buildFilterParams(appliedFilters);
      const response = await axios.get(`/api/admin/applications?${query}`, {
        headers: getAuthHeaders()
      });

      const data = response.data;
      setApplications(data.applications || data);
      setTotalApplications(data.total || data.length);
      setTotalPages(data.total_pages || Math.ceil((data.total || data.length) / perPage));
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      console.error('Error loading applications:', error);
    }
    setIsLoading(false);
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const updateRating = async (id, rating) => {
    try {
      await axios.put(
        `/api/admin/applications/${id}/rating`,
        { rating },
        { headers: getAuthHeaders() }
      );
      loadApplications();
    } catch (error) {
      console.error('Error updating rating:', error);
    }
  };

  const updateDescription = async (id, notes) => {
    try {
      await axios.put(
        `/api/admin/applications/${id}/notes`,
        { notes },
        { headers: getAuthHeaders() }
      );
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setExpandedId(null);
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
    setExpandedId(null);
  };

  const activeFilterCount = Object.values(appliedFilters).filter((v) => v !== '').length;
  const hasActiveFilters = activeFilterCount > 0;

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'ضعیف': return '#c94a5d';
      case 'متوسط': return '#d8d649';
      case 'خوب': return '#008cff';
      case 'عالی': return '#79d65d';
      default: return '#404040';
    }
  };

  const getTextColor = (rating) => {
    switch (rating) {
      case 'متوسط': return '#111';
      case 'ضعیف':
      case 'خوب':
      case 'عالی':
        return '#fff';
      default:
        return '#ccc';
    }
  };

  const goToPage = (page) => {
    setExpandedId(null);
    setCurrentPage(page);
  };

  const changePerPage = (newPerPage) => {
    setPerPage(parseInt(newPerPage, 10));
    setCurrentPage(1);
    setExpandedId(null);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage > 1) {
      pages.push(
        <button key="prev" type="button" onClick={() => goToPage(currentPage - 1)} className="pagination-btn">
          قبلی
        </button>
      );
    }

    if (startPage > 1) {
      pages.push(
        <button key={1} type="button" onClick={() => goToPage(1)} className="pagination-btn">1</button>
      );
      if (startPage > 2) pages.push(<span key="start-dots">...</span>);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          type="button"
          onClick={() => goToPage(i)}
          className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(<span key="end-dots">...</span>);
      pages.push(
        <button key={totalPages} type="button" onClick={() => goToPage(totalPages)} className="pagination-btn">
          {totalPages}
        </button>
      );
    }

    if (currentPage < totalPages) {
      pages.push(
        <button key="next" type="button" onClick={() => goToPage(currentPage + 1)} className="pagination-btn">
          بعدی
        </button>
      );
    }

    return (
      <div className="pagination-container">
        <div className="pagination">{pages}</div>
        <div className="page-info">
          صفحه {currentPage} از {totalPages} ({totalApplications} درخواست)
        </div>
      </div>
    );
  };

  return (
    <div className="container admin-page">
      <div className="admin-header application-header">
        <div>
          <h1>پنل مدیریت</h1>
          <p className="header-subtitle">بررسی و ارزیابی درخواست‌های استخدام</p>
        </div>
        <div className="admin-actions">
          <a href="/" className="admin-link-quiet">فرم درخواست</a>
          <button type="button" className="logout-btn" onClick={logout}>خروج</button>
        </div>
      </div>

      <div className="admin-tabs">
        <button
          type="button"
          className={`admin-tab ${activeTab === 'applications' ? 'active' : ''}`}
          onClick={() => setActiveTab('applications')}
        >
          درخواست‌ها
          {activeTab === 'applications' && totalApplications > 0 && (
            <span className="tab-count">{totalApplications}</span>
          )}
        </button>
        <button
          type="button"
          className={`admin-tab ${activeTab === 'jobtitles' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobtitles')}
        >
          عناوین شغلی
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'applications' && (
          <>
            <div className="admin-filters">
              <div className="filters-toolbar">
                <button
                  type="button"
                  className="filters-toggle"
                  onClick={() => setFiltersOpen((open) => !open)}
                >
                  فیلترها {hasActiveFilters ? `(${activeFilterCount})` : ''}
                  <span>{filtersOpen ? '▴' : '▾'}</span>
                </button>
                <div className="admin-controls-inline">
                  <button type="button" onClick={loadApplications} disabled={isLoading}>
                    {isLoading ? 'در حال بارگذاری...' : 'تازه‌سازی'}
                  </button>
                  <label className="per-page-label">
                    در هر صفحه
                    <select value={perPage} onChange={(e) => changePerPage(e.target.value)}>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </label>
                </div>
              </div>

              {filtersOpen && (
                <form onSubmit={applyFilters}>
                  <div className="filter-grid">
                    <div className="filter-field">
                      <label htmlFor="search">جستجو</label>
                      <input
                        id="search"
                        name="search"
                        type="text"
                        placeholder="نام، موبایل، ایمیل، رشته، مهارت"
                        value={filters.search}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <div className="filter-field">
                      <label htmlFor="job_id">عنوان شغلی</label>
                      <select id="job_id" name="job_id" value={filters.job_id} onChange={handleFilterChange}>
                        <option value="">همه</option>
                        {jobTitles.map((job) => (
                          <option key={job.id} value={job.id}>{job.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="rating">سطح ارزیابی</label>
                      <select id="rating" name="rating" value={filters.rating} onChange={handleFilterChange}>
                        <option value="">همه</option>
                        <option value="none">بدون ارزیابی</option>
                        <option value="ضعیف">ضعیف</option>
                        <option value="متوسط">متوسط</option>
                        <option value="خوب">خوب</option>
                        <option value="عالی">عالی</option>
                      </select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="education">تحصیلات</label>
                      <select id="education" name="education" value={filters.education} onChange={handleFilterChange}>
                        <option value="">همه</option>
                        <option value="دیپلم">دیپلم</option>
                        <option value="کاردانی">کاردانی</option>
                        <option value="لیسانس">لیسانس</option>
                        <option value="فوق لیسانس">فوق لیسانس</option>
                        <option value="دکتری">دکتری</option>
                      </select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="address">آدرس</label>
                      <input
                        id="address"
                        name="address"
                        type="text"
                        placeholder="آدرس"
                        value={filters.address}
                        onChange={handleFilterChange}
                      />
                    </div>

                    <div className="filter-field">
                      <label htmlFor="employment_type">نوع استخدام</label>
                      <select
                        id="employment_type"
                        name="employment_type"
                        value={filters.employment_type}
                        onChange={handleFilterChange}
                      >
                        <option value="">همه</option>
                        <option value="تمام وقت">تمام وقت</option>
                        <option value="پاره وقت">پاره وقت</option>
                        <option value="پروژه‌ای">پروژه‌ای</option>
                        <option value="کارآموزی">کارآموزی</option>
                      </select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="has_resume">فایل رزومه</label>
                      <select
                        id="has_resume"
                        name="has_resume"
                        value={filters.has_resume}
                        onChange={handleFilterChange}
                      >
                        <option value="">همه</option>
                        <option value="1">دارای رزومه</option>
                        <option value="0">بدون رزومه</option>
                      </select>
                    </div>

                    <div className="filter-field">
                      <label htmlFor="date_from">از تاریخ</label>
                      <DatePicker
                        id="date_from"
                        value={toJalaliPickerValue(filters.date_from)}
                        onChange={(date) =>
                          setFilters((prev) => ({
                            ...prev,
                            date_from: toApiDate(date)
                          }))
                        }
                        calendar={persian}
                        locale={persian_fa}
                        format="YYYY/MM/DD"
                        placeholder="انتخاب تاریخ"
                        calendarPosition="bottom-right"
                        inputClass="form-datepicker-input"
                        containerClassName="filter-datepicker"
                      />
                    </div>

                    <div className="filter-field">
                      <label htmlFor="date_to">تا تاریخ</label>
                      <DatePicker
                        id="date_to"
                        value={toJalaliPickerValue(filters.date_to)}
                        onChange={(date) =>
                          setFilters((prev) => ({
                            ...prev,
                            date_to: toApiDate(date)
                          }))
                        }
                        calendar={persian}
                        locale={persian_fa}
                        format="YYYY/MM/DD"
                        placeholder="انتخاب تاریخ"
                        calendarPosition="bottom-right"
                        inputClass="form-datepicker-input"
                        containerClassName="filter-datepicker"
                      />
                    </div>
                  </div>

                  <div className="filter-actions">
                    <button type="submit" disabled={isLoading}>اعمال فیلتر</button>
                    <button type="button" className="filter-clear-btn" onClick={clearFilters}>
                      پاک کردن
                    </button>
                    {hasActiveFilters && (
                      <span className="filter-active-hint">{activeFilterCount} فیلتر فعال</span>
                    )}
                  </div>
                </form>
              )}
            </div>

            {renderPagination()}

            <div className="app-list">
              {isLoading && applications.length === 0 ? (
                <div className="empty-state">در حال بارگذاری...</div>
              ) : applications.length === 0 ? (
                <div className="empty-state">
                  <strong>هیچ درخواستی یافت نشد</strong>
                  <p>فیلترها را تغییر دهید یا منتظر ثبت درخواست جدید بمانید.</p>
                </div>
              ) : (
                applications.map((app) => (
                  <ApplicationCard
                    key={app.id}
                    app={app}
                    expanded={expandedId === app.id}
                    onToggle={() => setExpandedId((id) => (id === app.id ? null : app.id))}
                    onUpdateRating={updateRating}
                    onUpdateNotes={updateDescription}
                    getRatingColor={getRatingColor}
                    getTextColor={getTextColor}
                  />
                ))
              )}
            </div>

            {renderPagination()}
          </>
        )}

        {activeTab === 'jobtitles' && <JobTitlesAdmin />}
      </div>
    </div>
  );
};

export default AdminPanel;
